import { useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { keyBy, mapValues } from 'lodash';

import Icon from '../../../components/icon/Icon';
import Loading from '../../../components/loading/Loading';
import { Button } from '../../../components/button';
import {
  useGetPricingByCategoryQuery,
  useBatchUpdatePricingMutation,
} from '../../../redux/rtk';
import { alertError, alertSuccess } from '../../../redux/actions';
import text from '../../../text';
import { captureError } from '../../../utilities/error';
import ConfirmModal from './ConfirmModal';
import EditableCell from './EditableCell';
import PricingHistory from './PricingHistory';
import {
  centsToDollars,
  deriveMemberPrice,
  dollarsToCents,
  formatMoney,
} from './money';

type PricingModalProps = {
  show: boolean;
  onHide: () => void;
  categoryId: number;
  icon: string;
  label: string;
};

const PricingModal = ({
  show,
  onHide,
  categoryId,
  icon,
  label,
}: PricingModalProps) => {
  const dispatch = useDispatch();
  const { data } = useGetPricingByCategoryQuery(categoryId);
  const [batchUpdatePricing, { isLoading: isSaving }] =
    useBatchUpdatePricingMutation();

  const [prices, setPrices] = useState<Record<number, number>>({});
  const [areas, setAreas] = useState<Record<number, number>>({});
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    if (!data) return;
    const byId = keyBy(data.rows, 'id');
    setPrices(mapValues(byId, 'price'));
    setAreas(mapValues(byId, 'area'));
  }, [data]);

  const changedRows = useMemo(
    () =>
      data
        ? data.rows.filter(
            (row) =>
              (prices[row.id] ?? row.price) !== row.price ||
              (areas[row.id] ?? row.area) !== row.area
          )
        : [],
    [data, prices, areas]
  );

  // Mirrors the server validator so typos fail on the button, not as a 400.
  const hasInvalidRows = changedRows.some((row) => {
    const price = prices[row.id] ?? row.price;
    const area = areas[row.id] ?? row.area;
    return (
      !Number.isInteger(price) || price < 0 || !Number.isInteger(area) || area <= 0
    );
  });

  const handleSave = async () => {
    try {
      await batchUpdatePricing({
        categoryId,
        data: {
          rows: changedRows.map((row) => ({
            pricingId: row.id,
            price: prices[row.id] ?? row.price,
            area: areas[row.id] ?? row.area,
          })),
        },
      }).unwrap();
      dispatch(
        alertSuccess(text('pricingUpdated'), {
          icon: 'circled-tick',
          className: 'pricing-success-banner',
        })
      );
      setConfirming(false);
      onHide();
    } catch (err) {
      captureError(err);
      setConfirming(false);
      dispatch(alertError(text('pricingUpdateFailed')));
    }
  };

  // Constant across a category's tiers, so shown once above the table.
  const memberDiscountPercent = data?.rows[0]?.memberDiscountPercent ?? 0;

  return (
    <>
      <Modal
        centered
        scrollable
        show={show}
        onHide={onHide}
        dialogClassName='PricingModal'
      >
        <Modal.Header closeButton>
          <div className='d-flex align-items-center flex-gap-12'>
            <Icon
              icon={icon}
              hasIconClass={false}
              className='pricing-modal__icon original'
            />
            <h3 className='green-1 m-0'>{label}</h3>
          </div>
        </Modal.Header>
        <Modal.Body>
          {/* Gate only on missing data so refetches update the table in place. */}
          {!data ? (
            <Loading />
          ) : (
            <>
              <div className='pricing-modal__discount'>
                <span className='label'>{text('memberDiscountPercent')}</span>
                <span className='value'>
                  {memberDiscountPercent.toFixed(2)}%
                </span>
              </div>
              <table className='pricing-modal__table'>
                <thead>
                  <tr>
                    <th>{text('areaSqm')}</th>
                    <th>{text('fullPriceExcGst')}</th>
                    <th>{text('memberSaving')}</th>
                    <th>{text('memberPriceExcGst')}</th>
                    <th>{text('memberPriceIncGst')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((row) => {
                    const price = prices[row.id] ?? row.price;
                    const area = areas[row.id] ?? row.area;
                    const { saving, memberPriceExGst, memberPriceIncGst } =
                      deriveMemberPrice(
                        price,
                        row.memberDiscountPercent,
                        data.gstPercent
                      );
                    return (
                      <tr key={row.id}>
                        <td className='editable'>
                          <EditableCell
                            value={area}
                            display={String(area)}
                            onCommit={(next) =>
                              setAreas((prev) => ({ ...prev, [row.id]: next }))
                            }
                          />
                        </td>
                        <td className='editable'>
                          <EditableCell
                            value={centsToDollars(price)}
                            display={formatMoney(price)}
                            onCommit={(next) =>
                              setPrices((prev) => ({
                                ...prev,
                                [row.id]: dollarsToCents(next),
                              }))
                            }
                          />
                        </td>
                        <td>{formatMoney(saving)}</td>
                        <td>{formatMoney(memberPriceExGst)}</td>
                        <td>{formatMoney(memberPriceIncGst)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <PricingHistory categoryId={categoryId} />
            </>
          )}
        </Modal.Body>
        <Modal.Footer className='justify-content-center flex-gap-12'>
          <Button
            variant='primary'
            disabled={changedRows.length === 0 || hasInvalidRows}
            onClick={() => setConfirming(true)}
          >
            {text('updatePricingDefaults')}
          </Button>
          <Button variant='link' onClick={onHide}>
            {text('cancel')}
          </Button>
        </Modal.Footer>
      </Modal>
      <ConfirmModal
        show={confirming}
        message={text('pricingChangeWarning')}
        loading={isSaving}
        onHide={() => setConfirming(false)}
        onConfirm={handleSave}
      />
    </>
  );
};

export default PricingModal;
